import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/cn";

/**
 * Workshop: InlineEditText — click-to-edit text field.
 *
 * Default display is a plain span. On click it switches to an auto-focused
 * input. Enter or blur confirms the edit (calls onSave if value changed and
 * non-empty). Escape reverts to the original value.
 */

interface InlineEditTextProps {
  value: string;
  onSave: (newValue: string) => void;
  maxLength?: number;
  className?: string;
  "data-testid"?: string;
}

export function InlineEditText({
  value,
  onSave,
  maxLength,
  className,
  "data-testid": testId = "inline-edit-text",
}: InlineEditTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep draft in sync when value prop changes externally
  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [value, isEditing]);

  function startEditing() {
    setDraft(value);
    setIsEditing(true);
  }

  function commit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    }
    setIsEditing(false);
  }

  function cancel() {
    setDraft(value);
    setIsEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  }

  // Auto-focus and select all text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        maxLength={maxLength}
        className={cn(
          "bg-transparent outline-none border-b border-accent/60 focus:border-accent",
          "text-primary transition-colors",
          className,
        )}
        data-testid={`${testId}-input`}
        aria-label="Edit text"
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={startEditing}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEditing();
        }
      }}
      className={cn("cursor-pointer text-primary hover:text-primary/80", className)}
      data-testid={testId}
      title="Click to edit"
    >
      {value}
    </span>
  );
}

export type { InlineEditTextProps };
